import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { UserService } from '../../api-services/AdminHRService/User/user-service.service';
import { DeviceInfoService } from '../../api-services/DeviceInfo/device-info.service';
import { PermissionService } from '../../api-services/Permission/permission.service';
import { AuthService } from '../../api-services/AdminHRService/Login/auth.service';
import { StorageService } from '../../guards/StorageService';
import { firstValueFrom } from 'rxjs';
import { ToasterService } from '../../services/toaster.service';

type LoginFormType = FormGroup<{
  email: FormControl<string>;
  password: FormControl<string>;
}>;

@Component({
  selector: 'app-login',
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './login.component.html',
  styleUrl: './login.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class LoginComponent implements OnInit {
  loginForm!: LoginFormType;
  submitted = false;
  loading = false;
  alertType: 'success' | 'danger' | '' = '';
  alertMessage = '';

  emailCtrl!: FormControl<string>;
  passwordCtrl!: FormControl<string>;

  latitude: string | null = null;
  longitude: string | null = null;
  isLocationAllowed = false;

  constructor(private fb: FormBuilder,
    private router: Router,
    private readonly userService: UserService,
    private readonly deviceInfoSerive: DeviceInfoService,
    private readonly authService: AuthService,
    private readonly permissionService: PermissionService,
    private readonly storageService: StorageService,
    private readonly toasterService: ToasterService
  ) { }

  ngOnInit(): void {
    this.loginForm = this.fb.nonNullable.group({
      email: ['', Validators.required],
      password: ['', Validators.required]
    });

    this.emailCtrl = this.loginForm.controls.email;
    this.passwordCtrl = this.loginForm.controls.password;

    // this.askLocation();
  }

  async askLocation(): Promise<void> {
    try {
      const location = await this.deviceInfoSerive.getLocation();

      this.latitude = location.latitude;
      this.longitude = location.longitude;
      this.isLocationAllowed = !!(this.latitude && this.longitude);

      if (!this.isLocationAllowed) {
        this.toasterService.error('Location required to continue');
      }

    } catch (error: any) {
      this.isLocationAllowed = false;

      if (error?.code === 1) {
        this.toasterService.error('Location permission denied');
      } else {
        this.toasterService.error('Unable to fetch location');
      }
    }
  }

  async onSubmit(): Promise<void> {
    this.submitted = true;
    this.alertMessage = '';
    this.alertType = '';

    if (this.loginForm.invalid || this.loading) {
      return;
    }

    // if (!this.isLocationAllowed) {
    //   await this.askLocation();

    //   if (!this.isLocationAllowed) {
    //     this.toasterService.error('Please allow location permission to login');
    //     return;
    //   }
    // }

    this.loading = true;

    try {
      const payload = this.createPayload(this.loginForm.getRawValue());
      const res = await firstValueFrom(this.userService.VerifyUser(payload));

      if (res?.statusCode === 200 && res?.data?.accessToken) {
        this.authService.setAccessToken(res.data.accessToken);
        this.storageService.setRefreshToken(res.data.refreshToken);
        this.storageService.setLoginDateTime(new Date().toISOString());
        this.authService.markAuthenticated();

        const userid = this.authService.getUser()?.employeeId;

        await this.deviceInfoSerive.trackLogin(Number(userid), {
          latitude: this.latitude,
          longitude: this.longitude
        });

        this.router.navigate(['/landing']);
        return;
      }

      this.handleLoginFailure();
    } catch {
      this.loading = false;
      // this.authService.logout();
      this.alertMessage = 'Invalid login. Please try again.';
      this.alertType = 'danger';
    }
  }


  private handleLoginFailure(): void {
    this.loading = false;
    // this.authService.logout();
    this.router.navigate(['/SignIn']);
  }

  createPayload(req: { email: string; password: string }) {
    const input = req.email.trim();

    const basePayload = {
      password: req.password,
      isEmployee: true
    };

    if (input.includes('@')) {
      return {
        ...basePayload,
        emailId: input,
        employeeCode: ''
      };
    }

    return {
      ...basePayload,
      emailId: '',
      employeeCode: input
    };
  }

  quickFill(email: string, password: string): void {
    this.loginForm.patchValue({ email, password });
  }

  get emailInvalid(): boolean {
    return this.submitted && this.emailCtrl.invalid;
  }

  get passwordInvalid(): boolean {
    return this.submitted && this.passwordCtrl.invalid;
  }
}